// apps/client/src/assets/service.ts — catalog id → model (§7.4, §8.8). Code references assets by catalog id only.
import { assetMap, type AssetEntry, type BuiltManifest, type CatalogId, type CodeBuilderId, type KitId } from '@whiskerton/content';
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { IS_DEV } from '../boot/env';
import { windowMaterial } from '../engine/lighting';
import { builderModules } from './builders.gen';
import { contactBlob } from './blob';
import { createIconCache, webglIconRenderer, type IconRenderer } from './icons';
import { centred, loadKitGltf, prepareToy } from './loader';
import { loadManifest, MissingManifestError } from './manifest';

export { MissingManifestError };
export interface AssetService {
  ready(): Promise<void>;                                     // GET /assets/manifest.json?b=<BUILD_ID>
  model(id: CatalogId, params?: Readonly<Record<string, string | number | boolean>>): Promise<THREE.Object3D>;
  /** Revision 10 (D-04): a kit piece of a code builder's entry (`parts[name]`), unscaled, no contact blob, with window panes. */
  part(id: CatalogId, name: string): Promise<THREE.Object3D>;
  rig(): Promise<{ scene: THREE.Object3D; clips: THREE.AnimationClip[] }>;  // 'rig.mini', SkeletonUtils.clone per call
  icon(id: CatalogId): Promise<string>;                       // data URL rendered from the model (M1-T13, assets/icons.ts)
  preload(ids: readonly CatalogId[]): Promise<void>;
}
export interface CodeBuilder {
  id: CodeBuilderId;   // 'house' serves 'code:house'; equals the file basename (§7.4)
  build(params: Readonly<Record<string, string | number | boolean>>, ctx: { assets: AssetService }): THREE.Object3D | Promise<THREE.Object3D>;
}
/** rev 4 / rev 7: the factory the Game uses. Every dependency is injectable (tests pass stubs); omitted ones take the defaults below. */
export interface AssetDeps {
  loadManifest(): Promise<BuiltManifest>;
  loadKit(url: string): Promise<{ scene: THREE.Object3D; animations: THREE.AnimationClip[] }>;
  builders: ReadonlyMap<string, CodeBuilder>; assets: ReadonlyMap<CatalogId, AssetEntry>; warn(msg: string): void;
  iconRenderer?: () => IconRenderer;                                             // M1-T13 (default: the WebGL renderer in assets/icons.ts)
}
export function entryTransform(e: AssetEntry): { scale: number; yaw: number; offset: readonly [number, number, number] } {
  return { scale: e.scale ?? 1, yaw: -(e.yaw ?? 0) * (Math.PI / 2), offset: e.offset ?? [0, 0, 0] };
}
let placeholderGeo: THREE.BufferGeometry | null = null;
let placeholderMat: THREE.MeshStandardMaterial | null = null;
function placeholder(): THREE.Object3D {
  placeholderGeo ??= new RoundedBoxGeometry(0.6, 0.6, 0.6, 3, 0.12).translate(0, 0.3, 0);
  placeholderMat ??= new THREE.MeshStandardMaterial({ color: '#FFF4E2', roughness: 0.85, metalness: 0 });
  const g = new THREE.Group();
  g.add(new THREE.Mesh(placeholderGeo, placeholderMat), contactBlob(0.9));
  g.userData.placeholder = true;
  return g;
}
/** 'kit:<kit>/<node>' → [kit, node], split on the first '/' only. */
function kitRef(src: string): [KitId, string] {
  const rest = src.slice(4), i = rest.indexOf('/');
  return [rest.slice(0, i) as KitId, rest.slice(i + 1)];
}
export function createAssetService(deps: Partial<AssetDeps> = {}): AssetService {
  const d: AssetDeps = {
    loadManifest, loadKit: loadKitGltf, assets: assetMap,
    builders: new Map((builderModules as readonly { builder: CodeBuilder }[]).map((m) => [m.builder.id, m.builder])),
    warn: (m) => { if (IS_DEV) console.warn(m); },
    ...deps,
  };
  let manifest: Promise<BuiltManifest> | null = null;
  const kits = new Map<KitId, Promise<{ scene: THREE.Object3D; animations: THREE.AnimationClip[] }>>();
  const warned = new Set<string>();
  const tinted = new Map<string, THREE.MeshStandardMaterial>();
  const warnOnce = (id: string, why: string) => { if (!warned.has(id)) { warned.add(id); d.warn(`asset ${id}: ${why}; using a placeholder`); } };
  const getManifest = () => {
    if (!manifest) { manifest = d.loadManifest(); manifest.catch(() => { manifest = null; }); }   // retry after a failed fetch
    return manifest;
  };
  const kit = async (k: KitId) => {
    const m = await getManifest();
    const built = m.kits[k];
    if (!built) throw new MissingManifestError();
    let p = kits.get(k);
    if (!p) { p = d.loadKit(built.url); kits.set(k, p); p.catch(() => kits.delete(k)); }
    return p;
  };
  const applyTint = (o: THREE.Object3D, tint: AssetEntry['tint']) => {
    if (tint) o.traverse((c) => {
      const mesh = c as THREE.Mesh;
      if (!mesh.isMesh) return;
      const swap = (m: THREE.Material) => {                       // cached per (material, hex) so tinted clones still batch and instance
        const hex = tint[m.name];
        if (!hex) return m;
        const key = `${m.uuid}:${hex}`;
        let n = tinted.get(key);
        if (!n) { n = (m as THREE.MeshStandardMaterial).clone(); n.color.set(hex); tinted.set(key, n); }
        return n;
      };
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map(swap) : swap(mesh.material);
    });
  };
  const finish = (o: THREE.Object3D, e: AssetEntry, kitModel: boolean): THREE.Object3D => {
    const { scale, yaw, offset } = entryTransform(e);
    applyTint(o, e.tint);
    o.scale.multiplyScalar(scale);
    o.rotation.y += yaw;
    const root = kitModel ? centred(o).wrap : (() => { const g = new THREE.Group(); g.add(o); return g; })();
    root.children[0]!.position.add(new THREE.Vector3(...offset));
    if ((e.shadow ?? 'blob') === 'blob') {
      const box = new THREE.Box3().setFromObject(root), size = box.getSize(new THREE.Vector3());
      root.add(contactBlob(Math.max(0.6, Math.max(size.x, size.z) * 1.2)));
    }
    root.userData.wind = e.wind;
    root.userData.shadow = e.shadow ?? 'blob';
    return root;
  };
  /** The named node of a built kit, or null when the kit doesn't have it (a kit missing from the manifest throws MissingManifestError).
   *  tools/assets wraps each source file in a root node named after it, so look at the roots before searching deeper. */
  const nodeIn = (scene: THREE.Object3D, node: string) => scene.children.find((c) => c.name === node) ?? scene.getObjectByName(node) ?? null;
  const kitNode = async (src: string): Promise<THREE.Object3D | null> => {
    const [k, node] = kitRef(src);
    return nodeIn((await kit(k)).scene, node);
  };
  const service: AssetService = {
    async ready() { await getManifest(); },
    async model(id, params = {}) {
      const entry = d.assets.get(id);
      if (!entry) { warnOnce(id, 'no asset entry'); return placeholder(); }
      if (entry.src.startsWith('code:')) {
        const b = d.builders.get(entry.src.slice(5));
        if (!b) { warnOnce(id, `no builder for ${entry.src}`); return placeholder(); }
        return finish(await b.build({ ...entry.params, ...params, catalogId: id }, { assets: service }), entry, false);
      }
      const src = await kitNode(entry.src);
      if (!src) { warnOnce(id, `kit node ${entry.src} not built`); return placeholder(); }
      return finish(prepareToy(src.clone(true)), entry, true);
    },
    async part(id, name) {                                             // revision 10 (D-04): the house's kit pieces
      const key = `${id}#${name}`;
      const p = d.assets.get(id)?.parts?.[name];
      if (!p) { warnOnce(key, 'no such part'); return placeholder(); }
      const src = await kitNode(p.src);
      if (!src) { warnOnce(key, `kit node ${p.src} not built`); return placeholder(); }
      const o = prepareToy(src.clone(true));
      applyTint(o, p.tint);
      o.rotation.y += -(p.yaw ?? 0) * (Math.PI / 2);
      const { wrap, size } = centred(o);                                // unscaled, base at y = 0, no contact blob: the builder sizes it
      // Window glow in the node's own units: children of the node, so centring moved them with it; added after measuring so the
      // panes (which sit proud of the walls) never change the piece's size or base.
      for (const [x, y, z, w, h, facing] of p.panes ?? []) {
        const pane = new THREE.Mesh(new THREE.PlaneGeometry(w, h), windowMaterial());
        pane.name = 'windowPane';
        pane.position.set(x, y, z);
        pane.rotation.y = -facing * (Math.PI / 2);
        o.add(pane);
      }
      wrap.userData.size = size;
      return wrap;
    },
    async rig() {
      const entry = d.assets.get('rig.mini');
      if (!entry || !entry.src.startsWith('kit:')) throw new Error('rig.mini asset entry missing');
      const [k, node] = kitRef(entry.src);
      const g = await kit(k);
      const src = nodeIn(g.scene, node) ?? g.scene;
      return { scene: prepareToy(cloneSkinned(src)), clips: g.animations };
    },
    icon: (id) => icons.icon(id),
    async preload(ids) {
      const ks = new Set<KitId>();
      for (const id of ids) {
        const e = d.assets.get(id);
        if (e?.src.startsWith('kit:')) ks.add(kitRef(e.src)[0]);
        for (const p of Object.values(e?.parts ?? {})) ks.add(kitRef(p.src)[0]);   // revision 10: parts' kits
      }
      await Promise.all([...ks].map(kit));
    },
  };
  const icons = createIconCache({ model: (id) => service.model(id), renderer: d.iconRenderer ?? webglIconRenderer });
  return service;
}
